// Reading and writing AMDM's C2 workbook in the browser.
//
// exceljs is loaded lazily: it is the same library apps/bff/src/catalog/
// medicine-import.ts reads xlsx with, its browser bundle is ~860 KB, and only
// this one screen ever needs it. `import()` keeps it out of the main chunk, so
// an operator who never imports never downloads it.
import { reasonsAllowedFor, startingReasonFor, type RegistrarKind } from '@simsm/shared-types';
import {
  C2_COLUMNS,
  C2_MAX_ROWS,
  isC2Column,
  normalizeHeader,
  type C2Column,
  type SheetRow,
} from './c2-template';

// exceljs's browser build is UMD, so the namespace may or may not be wrapped in
// a `default` depending on how the bundler interops it.
type ExcelJsModule = typeof import('exceljs');

async function loadExcelJs(): Promise<ExcelJsModule> {
  const mod = (await import('exceljs')) as ExcelJsModule & { default?: ExcelJsModule };
  return mod.default ?? mod;
}

/** An xlsx date cell arrives as a Date; the contract is written in text. */
function dateToText(value: Date): string {
  const iso = value.toISOString();
  return iso.slice(0, 10);
}

function cellToText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return dateToText(value);
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  const record = value as { text?: unknown; result?: unknown; richText?: { text: string }[] };
  if (Array.isArray(record.richText)) return record.richText.map((part) => part.text).join('');
  if (record.result !== undefined) return cellToText(record.result);
  if (typeof record.text === 'string') return record.text;
  return '';
}

export interface WorkbookRead {
  headers: string[];
  rows: SheetRow[];
}

export class EmptyWorkbookError extends Error {}

/**
 * The first worksheet, read as text. Header names are normalized (see
 * `normalizeHeader`) and anything the contract does not name is dropped rather
 * than carried — an extra column in an operator's own copy is not an error.
 */
export async function readC2Workbook(file: File): Promise<WorkbookRead> {
  const ExcelJS = await loadExcelJs();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const sheet = workbook.worksheets[0];
  if (!sheet) throw new EmptyWorkbookError('workbook has no worksheet');

  const headerRow = sheet.getRow(1);
  const columnAt = new Map<number, C2Column>();
  const headers: string[] = [];
  headerRow.eachCell({ includeEmpty: false }, (cell, columnNumber) => {
    const name = normalizeHeader(cellToText(cell.value));
    if (!isC2Column(name)) return;
    columnAt.set(columnNumber, name);
    headers.push(name);
  });

  const rows: SheetRow[] = [];
  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return;
    const cells: Partial<Record<C2Column, string>> = {};
    let hasValue = false;
    for (const [columnNumber, column] of columnAt) {
      const text = cellToText(row.getCell(columnNumber).value).trim();
      if (text !== '') hasValue = true;
      cells[column] = text;
    }
    // A trailing row of empty cells is how a spreadsheet ends, not a row the
    // operator wrote; counting it would report an error they cannot see.
    if (hasValue) rows.push({ line: rowNumber, cells });
  });

  return { headers, rows };
}

/**
 * The template's example row: a new lot bought, imported or donated in, so its
 * reason is the active Registrar's first inflow (CF-15: PURCHASE for a pharmacy,
 * IMPORT for a manufacturer or an importer, which may not report PURCHASE). An
 * example its own Registrar is refused for (REASON_NOT_ALLOWED) teaches the wrong
 * file.
 */
export function templateExample(registrarKind: RegistrarKind): Record<C2Column, string> {
  return {
    stock_date: '',
    cod_locatie: 'F-001',
    cod_medicament: '0000123456',
    lot: 'AB1234',
    data_expirare_lot: '2027-06',
    cantitate_curenta: '120',
    motiv: startingReasonFor('increase', registrarKind),
    comentariu: '',
    corecteaza_raportul: '',
  };
}

/** The worksheet that lists the reasons the template's Registrar may report. */
export const TEMPLATE_REASONS_SHEET = 'Motive';

/**
 * The reasons admitted in the file for this Registrar type (UC50-15, CF-15), in
 * the table's order. The same list the import checks the `motiv` column against.
 */
export function templateReasons(registrarKind: RegistrarKind): readonly string[] {
  return reasonsAllowedFor(registrarKind);
}

export const TEMPLATE_FILENAME = 'sablon-miscari-stoc.xlsx';

/**
 * The template the operator downloads. It is generated here rather than served,
 * because no endpoint offers one and inventing a backend for a nine-column
 * header row would be the wrong trade. The header row is exactly
 * `C2_COLUMNS`, so the file this produces is a file this screen can read back.
 */
export async function buildC2Workbook(
  registrarKind: RegistrarKind,
): Promise<InstanceType<ExcelJsModule['Workbook']>> {
  const ExcelJS = await loadExcelJs();
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet('miscari');
  sheet.columns = C2_COLUMNS.map((column) => ({ header: column, key: column, width: 22 }));
  sheet.getRow(1).font = { bold: true };
  sheet.addRow(templateExample(registrarKind));

  // UC50-11: the template carries the list of admitted reasons — as a sheet to
  // read, and as a drop-down on the `motiv` column so a typo cannot be entered.
  const reasons = templateReasons(registrarKind);
  const reasonSheet = workbook.addWorksheet(TEMPLATE_REASONS_SHEET);
  reasonSheet.columns = [{ header: 'motiv', key: 'motiv', width: 22 }];
  reasonSheet.getRow(1).font = { bold: true };
  for (const reason of reasons) reasonSheet.addRow({ motiv: reason });
  const motivColumn = C2_COLUMNS.indexOf('motiv') + 1;
  for (let rowNumber = 2; rowNumber <= C2_MAX_ROWS + 1; rowNumber += 1) {
    sheet.getCell(rowNumber, motivColumn).dataValidation = {
      type: 'list',
      allowBlank: true,
      formulae: [`"${reasons.join(',')}"`],
      showErrorMessage: true,
      errorTitle: 'motiv',
      error: reasons.join(', '),
    };
  }
  return workbook;
}

export async function buildC2Template(registrarKind: RegistrarKind): Promise<Blob> {
  const workbook = await buildC2Workbook(registrarKind);
  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/**
 * Hand a generated file to the browser. The anchor is created imperatively, not
 * as JSX: `mud-link` is a typographic link and has no download behaviour, and
 * R2's rule is about the elements a page renders.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
